import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Zachytí selhání líně načítaného kódu.
 *
 * Hra se stahuje až při spuštění tréninku. Když se mezitím nasadí nová verze,
 * dostanou soubory nová jména a starý požadavek skončí na 404 — dřív se v tu
 * chvíli aplikace jen sesypala do prázdné obrazovky. Totéž nastane při
 * krátkém výpadku sítě.
 *
 * Při první takové chybě se stránka jednou sama načte znovu; s novým
 * `index.html` přijdou i správná jména souborů. Když selže i to, ukáže se
 * srozumitelná hláška místo zamrznutí.
 */

/** Klíč v sessionStorage brání smyčce, kdyby reload problém nevyřešil. */
const RELOAD_FLAG = 'senses:chunk-reload'

interface Props {
  children: ReactNode
  /** Popis toho, co se nepodařilo načíst, do chybové hlášky. */
  what?: string
}

interface State {
  error: Error | null
  reloading: boolean
}

export class ChunkBoundary extends Component<Props, State> {
  override state: State = { error: null, reloading: false }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Načtení části aplikace selhalo', error, info.componentStack)

    if (!isChunkError(error)) return

    // Jeden pokus o obnovu za relaci. Bez pojistky by se stránka mohla
    // načítat dokola, pokud by chyba přetrvávala.
    if (sessionStorage.getItem(RELOAD_FLAG)) return

    sessionStorage.setItem(RELOAD_FLAG, '1')
    this.setState({ reloading: true })

    void refreshCachedModule(error).finally(() => window.location.reload())
  }

  private retry = (): void => {
    sessionStorage.removeItem(RELOAD_FLAG)
    window.location.reload()
  }

  override render(): ReactNode {
    const { error, reloading } = this.state
    if (!error) return this.props.children

    if (reloading) {
      return <div className="spinner">Načítám novou verzi…</div>
    }

    const chunk = isChunkError(error)

    return (
      <div className="play__intro">
        <div className="play__card">
          <div className="play__icon">🔌</div>
          <h2>{chunk ? 'Aplikace se mezitím aktualizovala' : 'Něco se nepovedlo'}</h2>

          <p className="play__desc" style={{ textAlign: 'center' }}>
            {chunk
              ? `Nepodařilo se načíst ${this.props.what ?? 'část aplikace'}, protože mezitím vyšla nová verze. Stačí stránku načíst znovu.`
              : 'Zkus stránku načíst znovu. Pokud potíže potrvají, dej vědět trenérovi.'}
          </p>

          <button type="button" className="btn btn--primary" onClick={this.retry}>
            Načíst znovu
          </button>

          <p className="field__help" style={{ marginTop: '1.25rem' }}>
            {error.message}
          </p>
        </div>
      </div>
    )
  }
}

/**
 * Přepíše záznam v cache prohlížeče čerstvou odpovědí ze serveru.
 *
 * Špatně nastavený web server umí na chybějící soubor vrátit index.html
 * se stavem 200 místo 404. S hlavičkou `immutable` si prohlížeč tu odpověď
 * uloží na rok — a i po opravě serveru pak dál dostává HTML tam, kde čeká
 * skript. Samotné načtení stránky s tím nic neudělá, protože se sáhne zase
 * do cache. Požadavek s `cache: 'reload'` server obejde a záznam přepíše.
 */
async function refreshCachedModule(error: Error): Promise<void> {
  const url = error.message.match(/https?:\/\/\S+?\.(?:js|mjs|css)/)?.[0]
  if (!url) return

  try {
    await fetch(url, { cache: 'reload', credentials: 'same-origin' })
  } catch {
    // Když se ani tohle nepovede, pomůže až obnovení stránky.
  }
}

/**
 * Selhalo stažení kódu, nebo jde o chybu v aplikaci?
 * Znění hlášky se mezi prohlížeči liší, proto ten výčet.
 */
function isChunkError(error: Error): boolean {
  const message = `${error.name} ${error.message}`.toLowerCase()
  return (
    message.includes('dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('failed to fetch') ||
    message.includes('loading chunk') ||
    message.includes('error loading dynamically imported module')
  )
}
